import React, { useCallback } from 'react';
import { Box } from 'braid-design-system';
import { CarouselItem } from './CarouselItem';
import { NavControlsWrapper } from './NavControlsWrapper';
import {
  carouselContainer,
  carouselChunk,
  carouselChunkStatic,
} from '../Carousel.css';
import { useCarouselAnimation } from '../hooks/useCarouselAnimation';
import type { CarouselChunk, AnimationDirection } from '../utils/carouselUtils';

interface DesktopCarouselProps {
  chunks: CarouselChunk[];
  currentChunkIndex: number;
  isAnimating: boolean;
  animatingToIndex: number | null;
  animationDirection: AnimationDirection;
  totalChunks: number;
  chunkSize: number;
  onLeftClick: (event: React.MouseEvent) => void;
  onRightClick: (event: React.MouseEvent) => void;
  onPaginationClick: (event: React.MouseEvent, chunkIndex: number) => void;
}

export const DesktopCarousel = React.memo<DesktopCarouselProps>(
  ({
    chunks,
    currentChunkIndex,
    isAnimating,
    animatingToIndex,
    animationDirection,
    totalChunks,
    chunkSize,
    onLeftClick,
    onRightClick,
    onPaginationClick,
  }) => {
    const { getChunkClass } = useCarouselAnimation({
      isAnimating,
      currentChunkIndex,
      animatingToIndex,
      animationDirection,
    });

    const renderChunk = useCallback(
      (chunkIndex: number) => {
        const chunk = chunks[chunkIndex];
        if (!chunk) {
          return null;
        }

        const cssClass = getChunkClass(chunkIndex);

        // Dynamic grid template columns based on chunkSize
        const gridStyle = {
          gridTemplateColumns: `repeat(${chunkSize}, 1fr)`,
        };

        return (
          <Box
            className={`${carouselChunk} ${cssClass}`}
            style={gridStyle}
            key={`chunk-${chunkIndex}`}
          >
            {chunk.map(({ item, key: itemKey }) => (
              <CarouselItem key={itemKey} item={item} />
            ))}
          </Box>
        );
      },
      [chunks, getChunkClass, chunkSize],
    );

    return (
      <NavControlsWrapper
        onLeftClick={onLeftClick}
        onRightClick={onRightClick}
        onPaginationClick={onPaginationClick}
        isAnimating={isAnimating}
        currentChunk={currentChunkIndex}
        totalChunks={totalChunks}
      >
        <Box className={carouselContainer} height="full">
          {/* Static chunk for height calculation only - invisible but provides height */}
          <Box
            className={carouselChunkStatic}
            style={{ gridTemplateColumns: `repeat(${chunkSize}, 1fr)` }}
          >
            {chunks[currentChunkIndex]?.map(({ item, key: itemKey }) => (
              <CarouselItem key={`static-${itemKey}`} item={item} />
            ))}
          </Box>

          {/* Render all chunks with proper positioning */}
          {chunks.map((_, chunkIndex) => renderChunk(chunkIndex))}
        </Box>
      </NavControlsWrapper>
    );
  },
);

DesktopCarousel.displayName = 'DesktopCarousel';
